// The remote itself: a handset body, the four pads, and a tab bar along the
// bottom edge.
//
// ── Two modes, one component ───────────────────────────────────────────────
//
//   floating   a panel that sits OVER whatever screen you were on, with an ×.
//              The desktop uses this: you are looking at a room, you pick up the
//              remote, you press some keys, you put it down — and the room never
//              went away. A remote that replaces the page is a page you have to
//              navigate back out of.
//
//   inline     the whole screen. That is what a phone is already shaped like, so
//              the body just fills it.
//
// ── Tabs at the BOTTOM ─────────────────────────────────────────────────────
// Where the Flutter app had them (remote_main_page.dart:322-331, a
// `bottomNavigationBar`), and where a thumb is. The pads change constantly and
// the tab bar does not, so it is the one fixed thing to aim at — putting it at
// the top would leave it furthest from the hand for the whole session.
//
// ⚠️ NOTHING IS OPTIMISTIC HERE. An IR blaster is open-loop: it flashes a code
// at the room and cannot know whether the television saw it. The footer says
// what was SENT and repeats the device's own ACK sentence verbatim — no tick, no
// "Done", nothing that would be believed. See core/hooks/useIrRemote.

import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle';
import { REMOTE_PADS } from './RemotePads';
import { useIrRemote } from '../../../core/hooks/useIrRemote';

export const HANDSET_WIDTH = 320;

export default function RemoteHandset({
  uniqueNumber,
  deviceName,
  initialStatus = '',
  onClose,
  floating = false,
}) {
  const [tab, setTab] = useState(0);
  const {
    send, lastSent, lastAck, liveAt,
  } = useIrRemote(uniqueNumber, initialStatus);

  const { Pad } = REMOTE_PADS[tab];

  const body = (
    <Box sx={{
      width: floating ? HANDSET_WIDTH : '100%',
      maxWidth: HANDSET_WIDTH,
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      /*
       * ── The case ────────────────────────────────────────────────────────
       * A moulded shell, not a panel. Four cues, and they have to agree with
       * the keys or the whole thing reads as flat:
       *
       *   • the gradient runs light at the TOP, so the light source is above —
       *     the same direction every key's lit edge and skirt assume
       *   • a bright inset hairline along the top edge and a dark one along the
       *     bottom: the rim of the shell catching that light
       *   • a faint dark inset all round, so the keys sit INSIDE a tray rather
       *     than floating on a rectangle
       *   • two outer shadows — a tight contact one and a wide ambient one.
       *     A single blurred shadow reads as a sticker; the pair reads as an
       *     object resting above the page.
       */
      color: '#E8ECF4',
      borderRadius: '30px',
      backgroundImage: [
        // a soft sheen across the upper third, as if lit from the front-left
        'radial-gradient(120% 60% at 30% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 60%)',
        'linear-gradient(180deg, #2C323E 0%, #232833 45%, #191D25 100%)',
      ].join(', '),
      boxShadow: [
        'inset 0 1px 0 rgba(255,255,255,0.16)', // top rim, lit
        'inset 0 -1px 0 rgba(0,0,0,0.55)', // bottom rim, in shade
        'inset 0 0 0 1px rgba(255,255,255,0.05)', // the shell's own edge
        'inset 0 0 24px rgba(0,0,0,0.35)', // the tray the keys sit in
        ...(floating ? [
          '0 2px 6px rgba(0,0,0,0.45)', // contact
          '0 28px 60px -12px rgba(0,0,0,0.65)', // ambient
        ] : []),
      ].join(', '),
      overflow: 'hidden',
      maxHeight: floating ? 'calc(100vh - 96px)' : '100%',
    }}
    >
      {/* Head: the IR window, the device, and the way out. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.75, pb: 1,
      }}
      >
        <Tooltip title={liveAt ? 'Reporting' : 'No reply heard this session'}>
          <CircleIcon sx={{ fontSize: 10, color: liveAt ? '#66BB6A' : 'rgba(232,236,244,0.3)' }} />
        </Tooltip>
        <Typography sx={{
          flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, letterSpacing: 0.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        >
          {deviceName || uniqueNumber}
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(232,236,244,0.7)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Keys. Scrolls inside the body, so the tab bar below never moves. */}
      <Box sx={{ px: 2, pb: 1.5, overflowY: 'auto', flex: 1 }}>
        <Pad onSend={send} />
      </Box>

      {/* What was sent, and the device's own words. Never a claim of success. */}
      <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <Typography sx={{ fontSize: 10, color: 'rgba(232,236,244,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lastSent ? `Sent ${lastSent.token}` : 'Press a key'}
          {lastAck ? ` · ${lastAck}` : ''}
        </Typography>
      </Box>

      {/* The tab bar, on the bottom edge of the handset — RECESSED, so it reads
          as a lower deck of the moulding rather than a strip laid on top. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${REMOTE_PADS.length}, 1fr)`,
        backgroundImage: 'linear-gradient(180deg, #14171D 0%, #1B1F27 100%)',
        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6), inset 0 1px 0 rgba(0,0,0,0.8)',
      }}
      >
        {REMOTE_PADS.map((p, i) => {
          const active = i === tab;
          return (
            <Box
              key={p.id}
              onClick={() => setTab(i)}
              sx={{
                py: 1.25,
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: active ? '#4FC3F7' : 'rgba(232,236,244,0.55)',
                borderTop: active ? '2px solid #4FC3F7' : '2px solid transparent',
                userSelect: 'none',
                '&:hover': { color: '#E8ECF4' },
              }}
            >
              {p.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (!floating) return body;

  return (
    <Box sx={{
      position: 'fixed',
      right: 24,
      bottom: 24,
      zIndex: (t) => t.zIndex.modal,
      // No backdrop on purpose: the point of a floating remote is that the room
      // behind it stays visible and usable while you press keys.
      pointerEvents: 'auto',
    }}
    >
      {body}
    </Box>
  );
}
